import { BaseGameController } from '@/components/game/implementations/_base/_domain/controller/game-controller';
import { injectable, inject } from 'inversify';
import { IServerServiceFactory } from '@/app-modules/server-service';
import { GameIdentity, IGameStageWinnerSet, GameState, IGamePrizeTicketBook } from '@/components/game/_domain/models';
import { WinnerCatchupData } from '@/components/game/_domain/server/winner-catchup/winner-catchup-data';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { SetStageData } from '@/components/game/_domain/server/set-stage';
import { IWinnersTrackerService, ITrackedWinner } from '@/components/game/_domain/modules/winners-tracker-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IConfigurationService } from '@/app-modules/config';
import { IErrorService } from '@/app-modules/error';
import { getPrizeTypeNameForLevel, getTicketBookForPrizeLevel, getTicketBookNameForTicketBookId } from '../models';

// tslint:disable-next-line:interface-name
interface GameStageWinnerSetsObject {
  [key: string]: IGameStageWinnerSet;
}

@injectable()
class TheMachineGameController extends BaseGameController {
  public constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) serverServiceFactory: IServerServiceFactory,
    @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) winnersTrackerService: IWinnersTrackerService,
    @inject(SERVICE_IDENTIFIERS.IErrorService) errorService: IErrorService
  ) {
    super(configurationService, serverServiceFactory, winnersTrackerService, errorService);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeCalls(this.handleCallsMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
      this.gameServerService.subscribePrizeWinner(this.handlePrizeWinnerMessage.bind(this)),
      this.gameServerService.subscribeWinnerCatchup(this.handleWinnerCatchUpMessage.bind(this)),
      this.gameServerService.subscribeSetStage(this.handleSetStageMessage.bind(this))
    );
  }

  protected handleWinnerCatchUpMessage(payload: WinnerCatchupData): void {
    const winners = this.getGameStageWinnerSetsFromWinnerCatchupData(payload);

    this.changeGameRepresentation({
      winners,
      isJackpotWon: payload.isJackpotWon()
    });
    if (winners.length > 0) {
      this.notifyGameWon();
      this.trackPrizeWinners(winners);
    }
  }

  protected handleSetStageMessage(payload: SetStageData): void {
    const displayWinnersForInMilliseconds = 20000;

    if (payload.newStage === 0 && this.game.representation.winners && this.game.representation.winners.length > 0) {
      this.notifyGameWon();
      this.changeGameState(GameState.WinnersDisplayed);

      setTimeout(() => {
        this.changeGameState(GameState.Purchasing);
      }, displayWinnersForInMilliseconds);
    }
  }

  protected handlePrizeWinnerMessage(payload: PrizeWinnerData): void {
    const winners = this.game.representation.winners || [];
    const payloadWinners = this.getGameStageWinnerSetsFromPrizeWinnerData(payload);

    this.changeGameRepresentation({
      winners: this.mergeGameStageWinnerSets(winners, payloadWinners),
      isJackpotWon: payload.jackpot
    });
    this.notifyGameWon();
    this.trackPrizeWinners(payloadWinners);
  }

  protected getGameStageWinnerSetsFromPrizeWinnerData(payload: PrizeWinnerData): IGameStageWinnerSet[] {
    const stageWinnerSets: GameStageWinnerSetsObject = {};
    payload.winners.forEach((winner) => {
      winner.prizeName = getPrizeTypeNameForLevel(winner.level);

      const gameStageWinnerSet = this.getGameStageWinnerSetForPrizeName(stageWinnerSets, winner.prizeName, winner.stage);
      const gamePrizeTicketBook = this.getGamePrizeTicketBook(gameStageWinnerSet, winner.level);
      gamePrizeTicketBook.winners.push({
        username: winner.username,
        prizeInPence: winner.prizeInPence
      });
    });

    return Object.keys(stageWinnerSets).map((prizeName) => stageWinnerSets[prizeName]);
  }

  private getGameStageWinnerSetsFromWinnerCatchupData(payload: WinnerCatchupData): IGameStageWinnerSet[] {
    const stageWinnerSets: GameStageWinnerSetsObject = {};
    payload.prizes.forEach((prize) => {
      prize.winners.forEach((winner) => {
        const gameStageWinnerSet = this.getGameStageWinnerSetForPrizeName(stageWinnerSets, winner.prizeName, prize.level);
        const gamePrizeTicketBook = this.getGamePrizeTicketBook(gameStageWinnerSet, prize.level);
        gamePrizeTicketBook.winners.push({
          username: winner.username,
          prizeInPence: winner.prizeInPence
        });
      });
    });

    return Object.keys(stageWinnerSets).map((prizeName) => stageWinnerSets[prizeName]);
  }

  private getGameStageWinnerSetForPrizeName(stageWinnerSets: GameStageWinnerSetsObject, winnerPrizeName: string, gameStage: number): IGameStageWinnerSet {
    let gameStageWinnerSet = stageWinnerSets[winnerPrizeName];
    if (!gameStageWinnerSet) {
      gameStageWinnerSet = {
        stage: gameStage,
        prizeName: winnerPrizeName,
        ticketBooks: []
      };
      stageWinnerSets[winnerPrizeName] = gameStageWinnerSet;
    }
    return gameStageWinnerSet;
  }

  private getGamePrizeTicketBook(gameStageWinnerSet: IGameStageWinnerSet, ticketBookLevel: number): IGamePrizeTicketBook {
    let gamePrizeTicketBook = gameStageWinnerSet.ticketBooks.find((ticketBook) => ticketBook.level === ticketBookLevel);
    if (!gamePrizeTicketBook) {
      const ticketBookId = getTicketBookForPrizeLevel(ticketBookLevel);
      gamePrizeTicketBook = {
        id: ticketBookId,
        level: ticketBookLevel,
        name: getTicketBookNameForTicketBookId(ticketBookId),
        winners: []
      };
      gameStageWinnerSet.ticketBooks.push(gamePrizeTicketBook);
    }
    return gamePrizeTicketBook;
  }

  protected trackPrizeWinners(winnerSets: IGameStageWinnerSet[]): void {
    const gameIdentity = this.game.gameIdentity;
    winnerSets.forEach((winnerSet) => {
      const prizeName = winnerSet.prizeName;
      (winnerSet.ticketBooks || []).forEach((ticketBook) => {
        const trackedWinners = (ticketBook.winners || []).map<ITrackedWinner>((winner) => ({
          gameName: gameIdentity.name,
          clubName: gameIdentity.clubName,
          username: winner.username,
          prizeInPence: winner.prizeInPence,
          prizeName
        }));
        this.winnersTrackerService.trackWinners(...trackedWinners);
      });
    });
  }
}

export { TheMachineGameController };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/the-machine/_domain/controller/game-controller.ts